/** Cache-key schema version. Bump when the digest schema, the rule-table version, or the coach
 *  prompt changes - it versions the WHOLE cache (ARCHITECTURE.md §5). */
export const COACH_KEY_VERSION = 'v1';
// ── canonical JSON ──────────────────────────────────────────────────────────────
// Deterministic serialization: object keys sorted lexicographically, `undefined`-valued keys omitted
// (so an absent optional and a present-undefined optional serialize identically), arrays preserved in
// order. This is what makes the same bucketed spot always hash to the same key.
export function canonicalJson(value) {
    if (value === null)
        return 'null';
    const t = typeof value;
    if (t === 'number')
        return Number.isFinite(value) ? JSON.stringify(value) : 'null';
    if (t === 'boolean' || t === 'string')
        return JSON.stringify(value);
    if (t === 'bigint')
        return JSON.stringify(value.toString());
    if (Array.isArray(value))
        return '[' + value.map((v) => canonicalJson(v)).join(',') + ']';
    if (t === 'object') {
        const obj = value;
        const keys = Object.keys(obj)
            .filter((k) => obj[k] !== undefined)
            .sort();
        return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalJson(obj[k])).join(',') + '}';
    }
    // undefined / function / symbol - not valid key material; serialize as null so it is at least stable.
    return 'null';
}
// ── SHA-256 (pure TS, synchronous, isomorphic) ──────────────────────────────────
// prettier-ignore
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
const rotr = (x, n) => (x >>> n) | (x << (32 - n));
/** SHA-256 of a UTF-8 string → lowercase hex. Standard FIPS 180-4; verified against Node crypto. */
export function sha256Hex(message) {
    const msg = new TextEncoder().encode(message);
    const bitLen = msg.length * 8;
    // pad: 0x80, then zeros, to 56 mod 64, then 64-bit big-endian length.
    const withPad = ((msg.length + 8) >> 6 << 6) + 64; // next multiple of 64 leaving room for length
    const buf = new Uint8Array(withPad);
    buf.set(msg);
    buf[msg.length] = 0x80;
    // 64-bit length; JS bit-ops are 32-bit so write the low 32 bits (message length here never exceeds
    // 2^32 bits for our tiny digests) and leave the high 32 bits zero.
    const dv = new DataView(buf.buffer);
    dv.setUint32(withPad - 4, bitLen >>> 0, false);
    dv.setUint32(withPad - 8, Math.floor(bitLen / 0x100000000), false);
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const w = new Uint32Array(64);
    for (let off = 0; off < withPad; off += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = dv.getUint32(off + i * 4, false);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
        }
        let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + S1 + ch + K[i] + w[i]) | 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) | 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) | 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) | 0;
        }
        h[0] = (h[0] + a) | 0;
        h[1] = (h[1] + b) | 0;
        h[2] = (h[2] + c) | 0;
        h[3] = (h[3] + d) | 0;
        h[4] = (h[4] + e) | 0;
        h[5] = (h[5] + f) | 0;
        h[6] = (h[6] + g) | 0;
        h[7] = (h[7] + hh) | 0;
    }
    let hex = '';
    for (let i = 0; i < 8; i++)
        hex += (h[i] >>> 0).toString(16).padStart(8, '0');
    return hex;
}
// ── the coach cache key ─────────────────────────────────────────────────────────
/** The exact §5 key subset, pulled out of the digest. Everything here is bucketed/enumerated - nothing
 *  that differs between strategically-identical spots (dollars, seats, exact %, mode, playersInHand,
 *  exact position, rangeShape, imbalances, heroPerceived) enters the key. drawFlags and firedRule ids
 *  are set-like, so they are sorted for stability (two spots with the same set in different order share
 *  one key). */
export function cacheKeyMaterial(d) {
    return {
        street: d.street,
        gameType: d.context.gameType,
        positionClass: d.context.positionClass,
        icmPressure: d.context.icmPressure,
        sprBucket: d.sprBucket,
        handClass: d.hero.handClass,
        drawFlags: [...d.hero.drawFlags].sort(),
        equityBucket: d.hero.equityBucket,
        equityDeltaBucket: d.hero.equityDeltaBucket,
        textureClass: d.board.textureClass,
        streetEvent: d.board.streetEvent,
        archetypeId: d.villain.archetypeId,
        recAction: d.recommendation.action,
        recSizingIntent: d.recommendation.sizingIntent,
        firedRuleIds: d.recommendation.firedRules.map((r) => r.id).sort(),
        userAction: d.userAction
            ? { action: d.userAction.action, gradeBand: d.userAction.gradeBand }
            : undefined,
    };
}
/** `coach:v1:<sha256(canonicalJson(keyMaterial))>` - the KV cache key for the coach explanation
 *  (ARCHITECTURE.md §5). Pure + deterministic: same bucketed digest → same key, in every runtime. */
export function coachCacheKey(digest) {
    return `coach:${COACH_KEY_VERSION}:` + sha256Hex(canonicalJson(cacheKeyMaterial(digest)));
}
//# sourceMappingURL=cacheKey.js.map